// ./src/contexts/ExpensesContext.tsx
//
// Context wrapper around the useExpenses hook.
//
// Purpose:
// - Allows sibling components (list, edit drawer, selection summary, toolbar)
//   to share the same expenses state without prop-drilling.
// - Each page creates its own <ExpensesProvider> with its own config,
//   so LogbookPage filters don't affect DashboardPage, etc.
// - The underlying useExpenses hook remains the data engine; this context
//   simply makes its return value available to the component tree.
//
// Usage:
//
//   // In a page component:
//   <ExpensesProvider autoLoad={false} pageSize={20}>
//     <LogbookPageContent />
//     <LogbookEditDrawer />
//   </ExpensesProvider>
//
//   // In any child component:
//   const { expenses, loading, create, ... } = useExpensesContext();
//
//   // In a component that may or may not be inside a provider (e.g. drawer):
//   const context = useOptionalExpensesContext();
//   const createFn = context?.create ?? props.onCreate;

import React, { createContext, useContext } from "react";

import { useExpenses, UseExpensesOptions, UseExpensesReturn } from "@/hooks/useExpenses";

// ============================================
// Context
// ============================================

const ExpensesContext = createContext<UseExpensesReturn | null>(null);

// ============================================
// Provider
// ============================================

export interface ExpensesProviderProps extends UseExpensesOptions {
  children: React.ReactNode;
}

/**
 * Wraps useExpenses into a context provider.
 *
 * Each instance creates its own independent useExpenses store
 * with its own filters, pagination, loading state, and error state.
 *
 * @example
 * <ExpensesProvider autoLoad={false} pageSize={20} initialSort={[{ name: "whenDone", order: "DESC" }]}>
 *   <MyPageContent />
 * </ExpensesProvider>
 */
export const ExpensesProvider: React.FC<ExpensesProviderProps> = ({
  children,
  ...options
}) => {
  const expenses = useExpenses(options);

  return (
    <ExpensesContext.Provider value={expenses}>
      {children}
    </ExpensesContext.Provider>
  );
};

// ============================================
// Hooks
// ============================================

/**
 * Access the ExpensesContext. Throws if used outside an ExpensesProvider.
 *
 * Use this in components that are always rendered inside a provider
 * (e.g. LogbookList, ExpenseFiltersToolbar).
 */
export const useExpensesContext = (): UseExpensesReturn => {
  const context = useContext(ExpensesContext);

  if (!context) {
    throw new Error(
      "useExpensesContext must be used within an <ExpensesProvider>. " +
      "Wrap your component tree with <ExpensesProvider> or use useOptionalExpensesContext() instead."
    );
  }

  return context;
};

/**
 * Access the ExpensesContext if available, or return null.
 *
 * Use this in components that may or may not be inside a provider
 * (e.g. LogbookEditDrawer which works both inside LogbookPage's provider
 * and standalone on DashboardPage).
 *
 * @example
 * const context = useOptionalExpensesContext();
 * const createFn = context?.create ?? props.onCreate;
 * const loadFn = context?.get ?? props.onLoad;
 */
export const useOptionalExpensesContext = (): UseExpensesReturn | null => {
  return useContext(ExpensesContext);
};